import breakpoints from 'style-utils/breakpoints';
import colors from 'style-utils/colors';
import media from 'style-utils/media-queries';
import { screenReaderOnly } from 'style/a11y.style';
import styled from 'styled-components';

export const FocusSentinel = styled.div`
  ${screenReaderOnly};
`;

type ModalContentProps = { hideFooter?: boolean };

export const ModalContent = styled.div<ModalContentProps>`
  display: block;
  /* Safari does not suppport the short-hand syntax. */
  overflow: hidden;
  background: ${colors.white};
  /* How to center an absolute 'div' https://stackoverflow.com/a/8273750 */
  /*
   * 'z-index' & position are required to create a new stacking context.
   * https://developer.mozilla.org/en-US/docs/Web/CSS/position */
  position: absolute;
  z-index: 2;
  left: 0;
  right: 0;
  margin: 0 auto;

  ${({ theme: { modal }, hideFooter }) => `
    box-shadow: ${modal.content.mobile.boxShadow};
    border-radius: ${modal.content.mobile.borderRadius};
    padding: ${modal.content.mobile.padding};
    top: ${modal.content.mobile.top};
    bottom: ${modal.content.mobile.bottom || modal.content.mobile.top};
    width: ${modal.content.mobile.width};
    transform: ${modal.content.mobile.transform};
  `};

  /* Unfortunately 'media' api provided by 'styled-bootstrap-grid'
   * does not support custom themes. */
  @media (min-width: ${breakpoints.md}px) {
    ${({ theme: { modal }, hideFooter }) => `
      padding: ${modal.content.tablet.padding};
      top: ${modal.content.tablet.top};
      bottom: ${modal.content.tablet.bottom || modal.content.tablet.top};
      width: ${modal.content.tablet.width};
      border-radius: ${modal.content.tablet.borderRadius};
      box-shadow: ${modal.content.tablet.boxShadow};
      transform: ${modal.content.tablet.transform};
    `};
  }

  @media (min-width: ${breakpoints.lg}px) {
    ${({ theme: { modal }, hideFooter }) => `
      padding: ${modal.content.desktop.padding};
      top: ${modal.content.desktop.top};
      bottom: ${modal.content.desktop.bottom || modal.content.desktop.top};
      width: ${modal.content.desktop.width};
      border-radius: ${modal.content.desktop.borderRadius};
      box-shadow: ${modal.content.desktop.boxShadow};
      transform: ${modal.content.desktop.transform};
      min-width: ${modal.content.desktop.minWidth};
    `};
  }
`;

export const ScrollableWrapper = styled.div<{
  autoMarginBottomManagement: boolean;
}>`
  height: 100%;
  overflow: auto;
  ${({ autoMarginBottomManagement }) =>
    `margin-bottom: ${autoMarginBottomManagement ? '72px' : ''};`}

  ${({ theme: { modal } }) => `
    max-height: ${modal.scrollableWrapper.mobile.maxHeight};
  `}

   @media (min-width: ${breakpoints.md}px) {
    ${({ theme: { modal }, autoMarginBottomManagement }) => `
          max-height: ${modal.scrollableWrapper.tablet.maxHeight};
          margin-bottom: ${autoMarginBottomManagement ? '0' : ''};
    `};
  }

  @media (min-width: ${breakpoints.lg}px) {
    ${({ theme: { modal } }) => `
          max-height: ${modal.scrollableWrapper.desktop.maxHeight};

    `};
  }
`;

export const ModalOverlay = styled.div`
  display: block;
  position: fixed;
  z-index: ${({ theme: { zIndexes } }) => zIndexes.modal.overlay};
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${({ theme }) => theme.modal.overlay.background};
`;

export const CloseButtonRoot = styled.button.attrs(() => ({
  type: 'button',
  'aria-label': 'close',
  title: 'close',
}))`
  position: absolute;
  flex: 0 0 auto;
  display: inline-block;
  line-height: 1;
  background: transparent;
  border: none;
`;

export const CloseButtonPrimaryVariantRoot = styled(CloseButtonRoot)`
  cursor: pointer;
  top: 0;
  right: 0;
  color: ${colors.white};
  /* We set a min amount of padding to make it easier to tap on mobile. */
  padding: 17px;

  ${media.desktop`
    padding: 22px;
  `};

  &:focus {
    outline-width: 0;
  }
`;

export const CloseButtonSecondaryVariantRoot = styled(CloseButtonRoot)`
  top: 25px;
  right: 20px;
  color: ${colors.black};
  /* We set a min amount of padding to make it easier to tap on mobile. */
  padding: 0 5px 5px;
`;
